import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const contract = initContract();

const userRecordSchema = z.object({
  id: z.string(),
  cognitoSub: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  profilePhotoUrl: z.string().nullable(),
  mfaEnrollmentRequired: z.boolean(),
});

const errorSchema = z.object({
  message: z.string(),
});

const emailSchema = z.string().trim().toLowerCase().min(1, 'Email is required').email('Enter a valid email address');

const changeEmailBodySchema = z.object({
  newEmail: emailSchema,
});

const confirmEmailChangeBodySchema = z.object({
  token: z.string().min(1, 'This email confirmation link is invalid or has expired.'),
});

const uploadProfilePhotoSchema = z.object({
  profilePhotoUrl: z.string().url(),
});

const successSchema = z.object({
  success: z.literal(true),
});

const updateUserBodySchema = z
  .object({
    firstName: z.string().min(1).max(100).optional(),
    lastName: z.string().min(1).max(100).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided',
  });

export const userRoutes = contract.router(
  {
    getUser: {
      method: 'GET',
      metadata: { access: { requiresUser: true } },
      path: '/api/user',
      responses: {
        200: userRecordSchema,
        401: errorSchema,
        404: errorSchema,
        500: errorSchema,
      },
      summary: 'Get the current user record',
      description:
        'Returns the persistent user row for the signed-in user, drawn from the users table in the active organization database. req.session.user.userId is the lookup key, falling back to the session cognitoSub/email; a signed-in user with no row yet is provisioned from the session identity. mfaEnrollmentRequired is the one field that is not a users column: it is the flag computed on the sessions row when the session was created, true when the organization requires two-factor authentication and this account has none, or when an administrator reset the account\'s second factor. The SPA reads it to land the user on the mandatory enrollment page; the server enforces it in the session middleware, which answers 403 for every path outside the enrollment allowlist — this endpoint is on that allowlist, so a gated session can still read it. Requires a valid SSO cookie with an active organization — otherwise 401.',
    },
    updateUser: {
      method: 'PUT',
      metadata: { access: { requiresUser: true } },
      path: '/api/user',
      body: updateUserBodySchema,
      responses: {
        200: successSchema,
        400: errorSchema,
        401: errorSchema,
        404: errorSchema,
        500: errorSchema,
      },
      summary: 'Update profile fields for the signed-in user',
      description:
        'Updates mutable fields (firstName, lastName) on the user row and refreshes the session snapshot. Theme lives on preferences. Only the fields included in the body are written. The response carries success only — the SPA refetches GET /api/user, so no user projection is returned.',
    },
    changeEmail: {
      method: 'POST',
      metadata: { access: { requiresUser: true } },
      path: '/api/user/email',
      body: changeEmailBodySchema,
      responses: {
        200: successSchema,
        400: errorSchema,
        409: errorSchema,
        500: errorSchema,
      },
      summary: 'Email a confirmation link to the new address for the signed-in user',
      description:
        'Starts an email change. Nothing changes yet: the server checks the new address is not the current one and that no Cognito account already holds it, then emails a one-hour signed confirmation link to the NEW address, proving the user controls it before it becomes their login and the address password-reset links go to. The link carries the Cognito sub, the new address and the account\'s Cognito last-modified time, and is redeemed by POST /api/user/email/confirm. The 200 carries success only — the client already holds the address it submitted and renders it in its "check your inbox" copy, and resending is submitting the same address again. 400 when the new address matches the current one, 409 when another account already owns it, 500 when the email cannot be sent.',
    },
    confirmEmailChange: {
      method: 'POST',
      metadata: { access: 'none' },
      path: '/api/user/email/confirm',
      body: confirmEmailChangeBodySchema,
      responses: {
        200: successSchema,
        400: errorSchema,
        409: errorSchema,
        500: errorSchema,
      },
      summary: 'Public — confirm an email change from the emailed link',
      description:
        'Redeems the signed link minted by POST /api/user/email. Public: the path is on the session-middleware allowlist, so it takes no session and never answers 401 — the link opened on another device is the credential. The link is single-use: it carries the account\'s Cognito last-modified time, and changing the email moves it. On success the new address is written to Cognito as the verified email (so it becomes the login), then to the users row in every organization the account belongs to, refreshing that organization\'s active sessions; an organization that cannot be reached is logged and skipped, since Cognito is the authority. The 200 carries success only. A forged, expired, already-used link, or one whose account is gone, is 400 \'This email confirmation link is invalid or has expired.\'; 409 when another account took the address after the link was sent. Error bodies carry a message only.',
    },
    uploadProfilePhoto: {
      method: 'POST',
      metadata: { access: { requiresUser: true } },
      path: '/api/user/profile-photo',
      contentType: 'multipart/form-data',
      body: z.object({}).optional(),
      responses: {
        200: uploadProfilePhotoSchema,
        400: errorSchema,
        500: errorSchema,
      },
      summary: 'Upload a new profile photo for the signed-in user',
      description:
        'Accepts a multipart/form-data request with field "photo". The server resizes the image with sharp, uploads it to the public S3 bucket under the encrypted user directory, and writes the resulting CDN URL onto the user row. Limits: 5MB, image/jpeg | image/png | image/gif.',
    },
    deleteProfilePhoto: {
      method: 'DELETE',
      metadata: { access: { requiresUser: true } },
      path: '/api/user/profile-photo',
      body: z.object({}).optional(),
      responses: {
        200: successSchema,
        404: errorSchema,
        500: errorSchema,
      },
      summary: 'Remove the signed-in user profile photo',
      description:
        'Deletes the S3 object backing the current profile-photo URL and clears the field on the user row. 404 when the user has no profile photo to delete.',
    },
  }
);

export type UserRecord = z.infer<typeof userRecordSchema>;
export type UpdateUserBody = z.infer<typeof updateUserBodySchema>;
